import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { Song, Setlist } from '../types/song';
import { transposeChordProContent, getTransposedKey } from '../services/transposeService';
import { 
  importChordPro as importChordProService,
  importFromUltimateGuitar as importFromUGService,
  importFromUrl as importFromUrlService,
  exportSongs as exportSongsService,
  importSongsFromJson,
  isAsyncImportResult,
  pollJobStatus
} from '../services/importExportService';
import { 
  getStorageService, 
  NotFoundError, 
  StorageQuotaExceededError, 
  ConflictError,
  StorageError 
} from '../services/storageService';

interface ImportingJob {
  jobId: string;
  url: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error?: string;
  createdAt: string;
}

interface SongStore {
  // State
  songs: Song[];
  setlists: Setlist[];
  currentSong: Song | null;
  currentSetlist: Setlist | null;
  selectedTransposition: number;
  isLoading: boolean;
  error: string | null;
  importingJobs: ImportingJob[];
  hasLoadedInitial: boolean;
  
  // Initialization
  loadSongs: () => Promise<void>;
  
  // Song Management
  addSong: (song: Omit<Song, 'id' | 'createdAt' | 'lastModified'>) => string;
  updateSong: (id: string, updates: Partial<Song>) => void;
  deleteSong: (id: string) => void;
  getSong: (id: string) => Song | undefined;
  searchSongs: (query: string) => Song[];
  
  // Tag Management
  addTag: (songId: string, tag: string) => void;
  removeTag: (songId: string, tag: string) => void;
  getAllTags: () => string[];
  
  // Transposition
  transposeSong: (songId: string, semitones: number) => Song | null;
  setTransposition: (semitones: number) => void;
  resetTransposition: () => void;
  
  // Import Functions
  importChordPro: (content: string, metadata?: Partial<Song>) => Promise<string>;
  importFromUltimateGuitar: (content: string, metadata?: Partial<Song>) => Promise<string>;
  importFromUrl: (url: string, useAsync?: boolean) => Promise<string>;
  
  // Import Job Management
  addImportingJob: (jobId: string, url: string) => void;
  updateImportingJob: (jobId: string, updates: Partial<ImportingJob>) => void;
  removeImportingJob: (jobId: string) => void;
  getImportingJobs: () => ImportingJob[];
  
  // Setlist Management
  createSetlist: (name: string, description?: string) => string;
  updateSetlist: (id: string, updates: Partial<Setlist>) => void;
  deleteSetlist: (id: string) => void;
  addSongToSetlist: (setlistId: string, songId: string) => void;
  removeSongFromSetlist: (setlistId: string, songId: string) => void;
  reorderSetlistSongs: (setlistId: string, fromIndex: number, toIndex: number) => void;
  
  // Current Selection
  setCurrentSong: (song: Song | null) => void;
  setCurrentSetlist: (setlist: Setlist | null) => void;
  
  // Utility
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  exportSongs: () => string;
  importSongs: (data: string) => void;
  fixSongsWithRawContent: () => number;
}

export const useSongStore = create<SongStore>()(
  persist(
    (set, get) => ({
      // Initial state
      songs: [],
      setlists: [],
      currentSong: null,
      currentSetlist: null,
      selectedTransposition: 0,
      isLoading: false,
      error: null,
      importingJobs: [],
      hasLoadedInitial: false, // Start as false until store is hydrated from localStorage

      // Initialization - no-op for local store
      loadSongs: async () => {
        // Local store uses localStorage via persist middleware
        // Songs are automatically loaded on initialization
        // hasLoadedInitial is now set by onRehydrateStorage
      },

      // Song Management
      addSong: (songData) => {
        const id = uuidv4();
        const now = new Date().toISOString();
        const song: Song = {
          ...songData,
          id,
          createdAt: now,
          lastModified: now,
        };
        set(state => ({ songs: [...state.songs, song] }));
        return id;
      },

      updateSong: (id, updates) => {
        set(state => ({
          songs: state.songs.map(song =>
            song.id === id
              ? { ...song, ...updates, lastModified: new Date().toISOString() }
              : song
          ),
          currentSong: state.currentSong?.id === id
            ? { ...state.currentSong, ...updates, lastModified: new Date().toISOString() }
            : state.currentSong
        }));
      },

      deleteSong: (id) => {
        set(state => ({
          songs: state.songs.filter(song => song.id !== id),
          currentSong: state.currentSong?.id === id ? null : state.currentSong,
          // Remove from all setlists
          setlists: state.setlists.map(setlist => ({
            ...setlist,
            songs: setlist.songs.filter(songId => songId !== id)
          }))
        }));
      },

      getSong: (id) => {
        return get().songs.find(song => song.id === id);
      },

      searchSongs: (query) => {
        const lowerQuery = query.toLowerCase();
        return get().songs.filter(song =>
          song.title.toLowerCase().includes(lowerQuery) ||
          song.artist.toLowerCase().includes(lowerQuery) ||
          song.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
        );
      },

      // Tag Management
      addTag: (songId, tag) => {
        set(state => ({
          songs: state.songs.map(song =>
            song.id === songId && !song.tags.includes(tag)
              ? { ...song, tags: [...song.tags, tag] }
              : song
          )
        }));
      },

      removeTag: (songId, tag) => {
        set(state => ({
          songs: state.songs.map(song =>
            song.id === songId
              ? { ...song, tags: song.tags.filter(t => t !== tag) }
              : song
          )
        }));
      },

      getAllTags: () => {
        const allTags = get().songs.flatMap(song => song.tags);
        return Array.from(new Set(allTags));
      },

      // Transposition
      transposeSong: (songId, semitones) => {
        const song = get().songs.find(s => s.id === songId);
        if (!song) return null;

        const transposedContent = transposeChordProContent(song.chordProContent, semitones);
        const newKey = getTransposedKey(song.originalKey, semitones);

        const transposedSong = {
          ...song,
          chordProContent: transposedContent,
          key: newKey
        };

        // If this is the current song, update it
        if (get().currentSong?.id === songId) {
          set({ currentSong: transposedSong });
        }

        return transposedSong;
      },

      setTransposition: (semitones) => {
        set({ selectedTransposition: semitones });
        const currentSong = get().currentSong;
        if (currentSong) {
          get().transposeSong(currentSong.id, semitones);
        }
      },

      resetTransposition: () => {
        set({ selectedTransposition: 0 });
        const currentSong = get().currentSong;
        if (currentSong) {
          const originalSong = get().songs.find(s => s.id === currentSong.id);
          if (originalSong) {
            set({ currentSong: originalSong });
          }
        }
      },

      // Import Functions
      importChordPro: async (content, metadata = {}) => {
        set({ isLoading: true, error: null });
        
        try {
          const result = await importChordProService(content, metadata);
          
          const song = {
            title: result.title,
            artist: result.artist,
            key: result.key,
            originalKey: result.key,
            chordProContent: result.chordPro,
            tags: metadata.tags || [],
            isPublic: metadata.isPublic || false,
            createdBy: metadata.createdBy || 'user',
            tempo: result.tempo,
            metadata: {
              source: 'import' as const,
              capo: result.capo,
              ...metadata.metadata
            }
          };

          set({ isLoading: false });
          return get().addSong(song);
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Import failed' 
          });
          throw error;
        }
      },

      importFromUltimateGuitar: async (content, metadata = {}) => {
        set({ isLoading: true, error: null });
        
        try {
          const result = await importFromUGService(content, metadata);
          
          const song = {
            title: result.title,
            artist: result.artist,
            key: result.key,
            originalKey: result.key,
            chordProContent: result.chordPro,
            tags: metadata.tags || ['imported', 'ultimate-guitar'],
            isPublic: metadata.isPublic || false,
            createdBy: metadata.createdBy || 'user',
            tempo: result.tempo,
            metadata: {
              source: 'ultimate-guitar' as const,
              capo: result.capo,
              ...metadata.metadata
            }
          };

          set({ isLoading: false });
          return get().addSong(song);
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Import failed' 
          });
          throw error;
        }
      },
      
      importFromUrl: async (url, useAsync = true) => {
        // Always use async mode for URL imports to ensure proper conversion
        useAsync = true;
        set({ isLoading: true, error: null });
        
        try {
          const result = await importFromUrlService(url, useAsync);
          
          // Check if this is an async job
          if (isAsyncImportResult(result)) {
            // Add the importing job to track it
            get().addImportingJob(result.jobId, url);
            
            // Start polling in the background
            pollJobStatus(result.jobId, (status) => {
              console.log('Import job status:', status);
              get().updateImportingJob(result.jobId, { status: status.status });
              
              if (status.status === 'completed' && status.result) {
                // Add the song when job completes
                const song = {
                  title: status.result.metadata.title,
                  artist: status.result.metadata.artist,
                  key: status.result.metadata.key || 'C',
                  originalKey: status.result.metadata.key || 'C',
                  chordProContent: status.result.chordPro,
                  tags: ['imported', 'from-url'],
                  isPublic: false,
                  createdBy: 'user',
                  tempo: status.result.metadata.tempo,
                  metadata: {
                    source: 'url' as const,
                    sourceUrl: url,
                    capo: status.result.metadata.capo
                  }
                };
                
                get().addSong(song);
                get().removeImportingJob(result.jobId);
              } else if (status.status === 'failed') {
                get().updateImportingJob(result.jobId, { 
                  status: 'failed', 
                  error: status.error || 'Import failed' 
                });
                // Remove failed job after a delay
                setTimeout(() => get().removeImportingJob(result.jobId), 5000);
              }
            }).catch(error => {
              console.error('Job polling error:', error);
              get().updateImportingJob(result.jobId, { 
                status: 'failed', 
                error: error.message 
              });
              setTimeout(() => get().removeImportingJob(result.jobId), 5000);
            });
            
            set({ isLoading: false });
            return result.jobId; // Return job ID instead of song ID for async imports
          } else {
            // Synchronous result
            const song = {
              title: result.title,
              artist: result.artist,
              key: result.key,
              originalKey: result.key,
              chordProContent: result.chordPro,
              tags: ['imported', 'from-url'],
              isPublic: false,
              createdBy: 'user',
              tempo: result.tempo,
              metadata: {
                source: 'url' as const,
                sourceUrl: url,
                capo: result.capo
              }
            };
            
            console.log('Storing song:', {
              title: song.title,
              hasChordProContent: !!song.chordProContent,
              chordProPreview: song.chordProContent?.substring(0, 100)
            });

            set({ isLoading: false });
            return get().addSong(song);
          }
        } catch (error) {
          set({ 
            isLoading: false, 
            error: error instanceof Error ? error.message : 'Import from URL failed' 
          });
          throw error;
        }
      },

      // Setlist Management
      createSetlist: (name, description) => {
        const id = uuidv4();
        const now = new Date().toISOString();
        const setlist: Setlist = {
          id,
          name,
          description,
          songs: [],
          createdAt: now,
          lastModified: now
        };
        set(state => ({ setlists: [...state.setlists, setlist] }));
        return id;
      },

      updateSetlist: (id, updates) => {
        set(state => ({
          setlists: state.setlists.map(setlist =>
            setlist.id === id
              ? { ...setlist, ...updates, lastModified: new Date().toISOString() }
              : setlist
          )
        }));
      },

      deleteSetlist: (id) => {
        set(state => ({
          setlists: state.setlists.filter(setlist => setlist.id !== id),
          currentSetlist: state.currentSetlist?.id === id ? null : state.currentSetlist
        }));
      },

      addSongToSetlist: (setlistId, songId) => {
        set(state => ({
          setlists: state.setlists.map(setlist =>
            setlist.id === setlistId
              ? { 
                  ...setlist, 
                  songs: [...setlist.songs, songId],
                  lastModified: new Date().toISOString()
                }
              : setlist
          )
        }));
      },

      removeSongFromSetlist: (setlistId, songId) => {
        set(state => ({
          setlists: state.setlists.map(setlist =>
            setlist.id === setlistId
              ? {
                  ...setlist,
                  songs: setlist.songs.filter(id => id !== songId),
                  lastModified: new Date().toISOString()
                }
              : setlist
          )
        }));
      },

      reorderSetlistSongs: (setlistId, fromIndex, toIndex) => {
        set(state => ({
          setlists: state.setlists.map(setlist => {
            if (setlist.id !== setlistId) return setlist;
            
            const newSongs = [...setlist.songs];
            const [movedSong] = newSongs.splice(fromIndex, 1);
            newSongs.splice(toIndex, 0, movedSong);
            
            return {
              ...setlist,
              songs: newSongs,
              lastModified: new Date().toISOString()
            };
          })
        }));
      },

      // Current Selection
      setCurrentSong: (song) => {
        set({ currentSong: song, selectedTransposition: 0 });
      },

      setCurrentSetlist: (setlist) => {
        set({ currentSetlist: setlist });
      },

      // Utility
      setError: (error) => {
        set({ error });
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      exportSongs: () => {
        const { songs, setlists } = get();
        return exportSongsService(songs, setlists);
      },

      importSongs: (data) => {
        try {
          const parsed = importSongsFromJson(data);
          set(state => ({
            songs: [...state.songs, ...parsed.songs],
            setlists: parsed.setlists ? [...state.setlists, ...parsed.setlists] : state.setlists
          }));
        } catch (error) {
          set({ error: 'Invalid import data format' });
        }
      },
      
      // Import Job Management
      addImportingJob: (jobId, url) => {
        const job: ImportingJob = {
          jobId,
          url,
          status: 'pending',
          createdAt: new Date().toISOString()
        };
        set(state => ({ importingJobs: [...state.importingJobs, job] }));
      },

      updateImportingJob: (jobId, updates) => {
        set(state => ({
          importingJobs: state.importingJobs.map(job =>
            job.jobId === jobId ? { ...job, ...updates } : job
          )
        }));
      },

      removeImportingJob: (jobId) => {
        set(state => ({
          importingJobs: state.importingJobs.filter(job => job.jobId !== jobId)
        }));
      },

      getImportingJobs: () => {
        return get().importingJobs;
      },
      
      // Debug helper to fix songs with raw Ultimate Guitar content
      fixSongsWithRawContent: () => {
        const songs = get().songs;
        console.log(`Checking ${songs.length} songs for raw content...`);
        
        const songsToFix = songs.filter(song => 
          song.chordProContent && (
            song.chordProContent.includes('[tab]') || 
            song.chordProContent.includes('[ch]')
          )
        );
        
        if (songsToFix.length === 0) {
          console.log('No songs need fixing');
          return 0;
        }
        
        console.log(`Found ${songsToFix.length} songs with raw Ultimate Guitar content:`);
        songsToFix.forEach(song => {
          console.log(`- ${song.title} by ${song.artist}`);
        });
        
        // Remove these broken songs
        songsToFix.forEach(song => {
          console.log(`Removing broken song: ${song.title} by ${song.artist}`);
          get().deleteSong(song.id);
        });
        
        console.log('Broken songs removed. Please re-import them.');
        return songsToFix.length;
      }
    }),
    {
      name: 'commonchord-songs', // Storage key
      partialize: (state) => ({
        songs: state.songs,
        setlists: state.setlists
      }), // Only persist songs and setlists
      onRehydrateStorage: () => (state) => {
        // This runs after the store has been rehydrated from localStorage
        if (state) {
          state.hasLoadedInitial = true;
        }
      }
    }
  )
);

// Expose store to window for debugging
if (typeof window !== 'undefined') {
  (window as any).useSongStore = useSongStore;
}